import { CreditCardIcon, EnvelopeIcon } from "@heroicons/react/24/solid"
import React, { useEffect, useMemo, useState } from "react"
import { useMutation, useQuery } from "urql"

import PaymentProviderWrapper from "../../components/payments/PaymentProviderWrapper"
import ClientSelector from "../../components/pro_portal/calendar/ClientSelector"
import { Button } from "../../components/shared/Buttons"
import CurrencyInput from "../../components/shared/CurrencyInput"
import { Label } from "../../components/shared/Inputs"
import { Banner } from "../../components/shared/Layout"
import LoadingSpinner from "../../components/shared/LoadingSpinner"
import { AnimatedModal } from "../../components/shared/Modal"
import { usePractice } from "../../contexts/PracticeContext"
import { formatPrice, unformatPrice } from "../../utils/utils"

import BookableEventPaymentModal from "./BookableEventPaymentModal"
import {
  canProcessRegistrationPayment,
  getPaymentStatusDisplay,
  shouldOpenPostAddPaymentWizard
} from "./bookableEventPaymentStatus"
import { useBookableEvents } from "./BookableEventsContext"
import ClientDetailsFlyout from "./ClientDetailsFlyout"
import {
  ADD_CLIENT_TO_BOOKABLE_EVENT_MUTATION,
  ADD_CLIENT_TO_BOOKABLE_EVENT_SERIES_MUTATION,
  PROCESS_REGISTRATION_PAYMENT_MUTATION,
  PROCESS_SERIES_REGISTRATION_PAYMENT_MUTATION
} from "./mutations"
import PostAddRegistrationPaymentWizard from "./PostAddRegistrationPaymentWizard"
import ProgramClientDetailsFlyout from "./ProgramClientDetailsFlyout"
import { BOOKABLE_EVENT_REGISTRANTS_QUERY, BOOKABLE_EVENT_SERIES_REGISTRANTS_QUERY } from "./queries"
import RequestEventRegistrationPaymentModal from "./RequestEventRegistrationPaymentModal"

export default function BookableEventRegistrants() {
  const { practice } = usePractice()
  const { bookableEvent, groupProgramEventsEnabled } = useBookableEvents()
  const programSeriesId = bookableEvent.bookableEventSeriesId || null
  const seriesIdForManagement = programSeriesId
  const seriesIdForEnrollment = groupProgramEventsEnabled ? programSeriesId : null
  const isSeries = Boolean(seriesIdForManagement)
  const [showAddClientModal, setShowAddClientModal] = useState(false)
  const [selectedClient, setSelectedClient] = useState(null)
  const [clientDetailsFlyoutVisible, setClientDetailsFlyoutVisible] = useState(false)
  const [postAddRegistration, setPostAddRegistration] = useState(null)
  const [showPostAddWizard, setShowPostAddWizard] = useState(false)
  const [listPaymentRegistration, setListPaymentRegistration] = useState(null)
  const [showListPaymentModal, setShowListPaymentModal] = useState(false)
  const [showListRequestModal, setShowListRequestModal] = useState(false)

  const [{ data, fetching, error }, reexecuteQuery] = useQuery({
    query: seriesIdForManagement ? BOOKABLE_EVENT_SERIES_REGISTRANTS_QUERY : BOOKABLE_EVENT_REGISTRANTS_QUERY,
    variables: { id: seriesIdForManagement || bookableEvent.id }
  })

  const registrantsSource = seriesIdForManagement ? data?.bookableEventSeries : data?.bookableEvent
  const registrations = registrantsSource?.approvedRegistrations || []
  const clientCount = useMemo(() => registrations.length, [registrations.length])

  const atCapacity = useMemo(
    () => bookableEvent.capacity && clientCount >= bookableEvent.capacity,
    [bookableEvent.capacity, clientCount]
  )

  const handleAddClient = () => {
    setShowAddClientModal(true)
  }

  const handleAddClientSuccess = ({ registration, paymentResult } = {}) => {
    reexecuteQuery({ requestPolicy: "network-only" })
    setShowAddClientModal(false)

    if (shouldOpenPostAddPaymentWizard({ registration, paymentResult })) {
      setPostAddRegistration(registration)
      setShowPostAddWizard(true)
    }
  }

  const handleEditClient = (client) => {
    setSelectedClient(client)
    setClientDetailsFlyoutVisible(true)
  }

  const handleClientRemoved = () => {
    reexecuteQuery({ requestPolicy: "network-only" })
  }

  const handleListPaymentUpdated = () => {
    reexecuteQuery({ requestPolicy: "network-only" })
    setShowListPaymentModal(false)
    setShowListRequestModal(false)
    setListPaymentRegistration(null)
  }

  return (
    <div>
      {fetching ? (
        <LoadingSpinner />
      ) : error ? (
        <p>Error loading registrants</p>
      ) : (
        <>
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-lg font-semibold">
              {clientCount} / {bookableEvent?.capacity === null ? "∞" : bookableEvent?.capacity}{" "}
              {seriesIdForManagement ? "Program Enrollees" : "Registrants"}
            </h3>
            <Button type="primary" onClick={handleAddClient}>
              Add New
            </Button>
          </div>

          <div className="border-t border-gray-light">
            {clientCount > 0 ? (
              registrations.map((registration, index) => {
                const client = registration.client
                const paymentStatus = getPaymentStatusDisplay(registration.paymentStatus, {
                  bookedBy: registration.bookedBy
                })
                const showPaymentCtas = canProcessRegistrationPayment(registration)

                return (
                  <div
                    key={registration.id}
                    className={`${index === clientCount - 1 ? "" : "border-b border-gray-light"} py-4`}>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="font-bold">
                          {client.firstName} {client.lastName}
                        </div>
                        <div className="text-gray-dark">{client.email || client.phone}</div>
                        <span className={`mt-1 inline-block text-sm ${paymentStatus.color}`}>
                          {paymentStatus.label}
                        </span>
                        {showPaymentCtas && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {practice?.hasPaymentProvider && (
                              <Button
                                type="tertiary"
                                className="inline-flex items-center"
                                onClick={() => {
                                  setListPaymentRegistration(registration)
                                  setShowListPaymentModal(true)
                                }}>
                                <CreditCardIcon className="mr-1 size-4" />
                                Charge
                              </Button>
                            )}
                            <Button
                              type="tertiary"
                              className="inline-flex items-center"
                              onClick={() => {
                                setListPaymentRegistration(registration)
                                setShowListRequestModal(true)
                              }}>
                              <EnvelopeIcon className="mr-1 size-4" />
                              Request
                            </Button>
                          </div>
                        )}
                      </div>
                      <Button type="link" onClick={() => handleEditClient(client)}>
                        Edit
                      </Button>
                    </div>
                  </div>
                )
              })
            ) : (
              <div className="py-4 text-center text-gray-dark">No registrants yet for this event.</div>
            )}
          </div>

          <AnimatedModal
            visible={showAddClientModal}
            hideModal={() => setShowAddClientModal(false)}
            header={seriesIdForEnrollment ? "Add Client to Program" : "Add Client to Event"}>
            <AddClientToEvent
              bookableEvent={bookableEvent}
              seriesId={seriesIdForEnrollment}
              onSuccess={handleAddClientSuccess}
              onCancel={() => setShowAddClientModal(false)}
              atCapacity={atCapacity}
            />
          </AnimatedModal>

          <PostAddRegistrationPaymentWizard
            visible={showPostAddWizard}
            hideModal={() => {
              setShowPostAddWizard(false)
              setPostAddRegistration(null)
            }}
            registration={postAddRegistration}
            isSeries={Boolean(seriesIdForEnrollment)}
            onCompleted={() => {
              reexecuteQuery({ requestPolicy: "network-only" })
              setPostAddRegistration(null)
            }}
          />

          {listPaymentRegistration && (
            <>
              {practice?.hasPaymentProvider && (
                <PaymentProviderWrapper practice={practice} client={listPaymentRegistration.client}>
                  <BookableEventPaymentModal
                    visible={showListPaymentModal}
                    hideModal={() => {
                      setShowListPaymentModal(false)
                      setListPaymentRegistration(null)
                    }}
                    client={listPaymentRegistration.client}
                    registration={listPaymentRegistration}
                    onPaymentSuccess={handleListPaymentUpdated}
                    processPaymentMutation={
                      isSeries ? PROCESS_SERIES_REGISTRATION_PAYMENT_MUTATION : PROCESS_REGISTRATION_PAYMENT_MUTATION
                    }
                    processPaymentField={isSeries ? "processSeriesRegistrationPayment" : "processRegistrationPayment"}
                  />
                </PaymentProviderWrapper>
              )}
              <RequestEventRegistrationPaymentModal
                visible={showListRequestModal}
                hideModal={() => {
                  setShowListRequestModal(false)
                  setListPaymentRegistration(null)
                }}
                client={listPaymentRegistration.client}
                registration={listPaymentRegistration}
                isSeries={isSeries}
              />
            </>
          )}

          {selectedClient && seriesIdForManagement ? (
            <ProgramClientDetailsFlyout
              visible={clientDetailsFlyoutVisible}
              closeFlyout={() => setClientDetailsFlyoutVisible(false)}
              client={selectedClient}
              bookableEventSeriesId={seriesIdForManagement}
              onClientRemoved={handleClientRemoved}
            />
          ) : (
            selectedClient && (
              <ClientDetailsFlyout
                visible={clientDetailsFlyoutVisible}
                closeFlyout={() => setClientDetailsFlyoutVisible(false)}
                client={selectedClient}
                bookableEventId={bookableEvent.id}
                onClientRemoved={handleClientRemoved}
              />
            )
          )}
        </>
      )}
    </div>
  )
}

const AddClientToEvent = ({ bookableEvent, seriesId, onSuccess, onCancel, atCapacity }) => {
  const { practice } = usePractice()
  const defaultAmountCents = bookableEvent?.amountCents ?? 0
  const [selectedClient, setSelectedClient] = useState(null)
  const [amountCents, setAmountCents] = useState(defaultAmountCents)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [{ fetching }, addClientToEvent] = useMutation(ADD_CLIENT_TO_BOOKABLE_EVENT_MUTATION)
  const [{ fetching: seriesFetching }, addClientToSeries] = useMutation(ADD_CLIENT_TO_BOOKABLE_EVENT_SERIES_MUTATION)

  useEffect(() => {
    setAmountCents(defaultAmountCents)
  }, [defaultAmountCents])

  const handleClientSelect = (client) => {
    setSelectedClient(client)
  }

  const handleAddClient = async () => {
    if (!selectedClient) {
      setError("Please select a client first")
      return
    }

    setLoading(true)
    setError(null)

    try {
      const variables = seriesId
        ? { bookableEventSeriesId: seriesId, amountCents }
        : { bookableEventId: bookableEvent.id, amountCents }

      if (selectedClient.id) {
        variables.clientId = selectedClient.id
      } else {
        variables.clientParams = {
          firstName: selectedClient.firstName,
          lastName: selectedClient.lastName,
          email: selectedClient.email,
          phone: selectedClient.phone
        }
      }

      const result = seriesId ? await addClientToSeries(variables) : await addClientToEvent(variables)

      const successField = seriesId ? "addClientToBookableEventSeries" : "addClientToBookableEvent"
      const payload = result.data?.[successField]
      if (payload?.result === "success") {
        onSuccess({
          registration: payload.registration,
          paymentResult: payload.paymentResult
        })
      } else {
        const errorMessage = payload?.errors?.[0] || `Failed to add client to ${seriesId ? "program" : "event"}`
        setError(errorMessage)
      }
    } catch (err) {
      setError(err.message || "Failed to add client to event")
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="py-4">
      <div style={{ position: "relative", zIndex: 20000 }}>
        <ClientSelector defaultClient={null} onSelect={handleClientSelect} />
      </div>

      <div className="mt-4">
        <Label htmlFor="event-registration-price">Price for this client</Label>
        <CurrencyInput
          id="event-registration-price"
          placeholder="Price"
          value={formatPrice(amountCents, practice?.defaultCurrency, { alwaysShowDecimals: true }) || ""}
          onChange={(e) => setAmountCents(unformatPrice(e.target.value))}
        />
      </div>

      {amountCents > 0 && (
        <Banner type="info" className="mt-4">
          Adding a client does not charge them. After adding, you can request payment, process a card, or skip for now.
        </Banner>
      )}

      {atCapacity && (
        <Banner type="warning">
          Just a reminder: this event is at capacity, but you can add more clients if you want.
        </Banner>
      )}

      {error && <div className="mt-2 text-red">{error}</div>}

      <div className="mt-4 flex justify-end space-x-2">
        <Button type="tertiary" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          type="primary"
          onClick={handleAddClient}
          disabled={loading || !selectedClient || fetching || seriesFetching}>
          {loading || fetching || seriesFetching ? "Adding..." : "Add Client"}
        </Button>
      </div>
    </div>
  )
}
